import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'
import type { User } from '@/types'
import { authService } from '@/services/auth.service'
import { getFingerprint, getIamAccessToken, clearFingerprint } from '@/api'

interface AuthState {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  login: (user: User) => void
  resetAuth: () => void
  logout: () => Promise<void>
  setUser: (user: User) => void
  setLoading: (loading: boolean) => void
  setError: (error: string | null) => void
  clearError: () => void
  validateSession: () => Promise<void>
}

export const useAuthStore = create<AuthState>()(
  devtools(
    persist(
      (set) => ({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,

        login: (user: User) => {
          set({
            user,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          })
        },

        resetAuth: () => {
          clearFingerprint()
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
          })
        },

        logout: async () => {
          try {
            await authService.logout()
          } catch {
            clearFingerprint()
          } finally {
            set({
              user: null,
              isAuthenticated: false,
              isLoading: false,
              error: null,
            })
          }
        },

        setUser: (user: User) => set({ user }),

        setLoading: (loading: boolean) => set({ isLoading: loading }),

        setError: (error: string | null) => set({ error, isLoading: false }),

        clearError: () => set({ error: null }),

        validateSession: async () => {
          const fp = getFingerprint()
          const jwt = getIamAccessToken()
          if (!fp || !jwt) {
            set({
              user: null,
              isAuthenticated: false,
              isLoading: false,
            })
            return
          }

          set({ isLoading: true, error: null })
          try {
            const res = await authService.getCurrentUser()
            const fpAfter = getFingerprint()
            const jwtAfter = getIamAccessToken()
            if (fpAfter !== fp || jwtAfter !== jwt) {
              set({ isLoading: false })
              return
            }

            if (res.success && res.data) {
              set({
                user: res.data,
                isAuthenticated: true,
                isLoading: false,
              })
            } else {
              set({
                user: null,
                isAuthenticated: false,
                isLoading: false,
              })
              clearFingerprint()
            }
          } catch {
            const fpAfter = getFingerprint()
            const jwtAfter = getIamAccessToken()
            if (fpAfter !== fp || jwtAfter !== jwt) {
              set({ isLoading: false })
              return
            }
            set({
              user: null,
              isAuthenticated: false,
              isLoading: false,
            })
            clearFingerprint()
          }
        },
      }),
      {
        name: 'pod-leap-auth-storage',
        partialize: (state) => ({
          user: state.user,
          isAuthenticated: state.isAuthenticated,
        }),
      }
    ),
    { name: 'pod-auth-store' }
  )
)
